import { InstagramIntegration } from 'api/clients/ExternalIntegrationsClient'
import Styles from './InstagramPreview.module.scss'
import { BookmarkIcon, CommentIcon, LikeIcon, ShareIcon } from './icons'
import Silhouette from './silhouette.jpg'
import React, { ReactNode, RefObject, useEffect, useMemo, useRef, useState } from 'react'
import { CarouselDots } from './CarouselDots'
import { MoreButton } from '../components/MoreButton/MoreButton'
import { useTruncateText } from '../hooks/useTruncateText'
import { Carousel } from 'components/Carousel/Carousel'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { getIntegrationDetails } from '../getIntegrationDetails'
import { PollingPreview } from '../components/PollingPreview/PollingPreview'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Skeleton } from 'components/Skeleton/Skeleton'
import cx from 'classnames'
import { highlightContent } from '../highlightContent'
import { ConnectButton } from 'components/ArticlePostPreview/ConnectButton/ConnectButton'
import { MediaObject } from 'types'
import { MediaRenderer } from '../components/MediaRenderer/MediaRenderer'
import { Edit1 } from 'blaze-ui/icons'
import { Button } from 'blaze-ui/Button'
import * as CSS from 'csstype'
import { BlankMediaPlaceholder } from '../components/MediaRenderer/BlankMediaPlaceholder'
import { calculateAspectRatio, isDesignMediaObject } from './utils'
import { EditCaptionOverlay } from '../components/EditCaptionOverlay/EditCaptionOverlay'
import { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { CaptionSkeleton } from '../components/CaptionSkeleton/CaptionSkeleton'
import { EmblaCarouselType } from 'embla-carousel'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import { useHover } from '@react-aria/interactions'
import { DesignChannel } from 'components/NewFileModal/Types'
import { useOptionalCurrentSlide } from 'screens/Calendar/FullScreenPreview/CurrentSlideContext'
import {
  PreviewCarouselArrows,
  PREVIEW_CAROUSEL_HOVER_TARGET,
} from '../components/PreviewCarouselArrows/PreviewCarouselArrows'

interface InstagramPreviewProps {
  integration?: InstagramIntegration
  content: string
  mediaObjects?: MediaObject[]
  loading?: boolean
  emptyStateContent?: React.ReactNode
  pollContentStream?: boolean
  hideConnectButton?: boolean
  onEdit?: () => void
  containerClassName?: string
  customPreviewContent?: ReactNode
  maxWidth?: number | null
  showVideoControls?: boolean
  videoFit?: 'contain' | 'cover'
  isActive?: boolean
  isInsertingMedia?: boolean
  isTransitioning?: boolean
  isGenerating?: boolean
  showHoverActionOverlay?: boolean
  almanacFileKey?: string
  onClose?: () => void
  scheduledPost?: ScheduledPostShow
  alwaysTruncateCaption?: boolean
  captionElement?: React.ReactNode
  defaultName?: string
  usernameContentSameLine?: boolean
  postPreviewHeaderRef?: RefObject<HTMLDivElement>
  postPreviewContentRef?: RefObject<HTMLDivElement>
  postPreviewFooterRef?: RefObject<HTMLDivElement>
  onSeeMoreToggle?: (expanded: boolean) => void
  activePlatform?: ExternalIntegrationEnum
}

export const InstagramPreview = ({
  mediaObjects,
  content,
  integration,
  loading,
  emptyStateContent,
  pollContentStream = true,
  hideConnectButton,
  onEdit,
  containerClassName,
  customPreviewContent,
  showVideoControls,
  videoFit,
  maxWidth = 470,
  isActive = true,
  isInsertingMedia = false,
  isTransitioning = false,
  isGenerating = false,
  showHoverActionOverlay = false,
  almanacFileKey,
  onClose,
  scheduledPost,
  alwaysTruncateCaption = false,
  captionElement = null,
  defaultName,
  usernameContentSameLine = false,
  postPreviewHeaderRef,
  postPreviewContentRef,
  onSeeMoreToggle,
  activePlatform,
}: InstagramPreviewProps) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const postRef = useRef<HTMLDivElement>(null)
  const [embla, setEmbla] = useState<EmblaCarouselType | null>(null)
  const carouselPlugins = useMemo(() => [WheelGesturesPlugin()], [])
  const { name, picture } = getIntegrationDetails(integration, defaultName)
  const [imageIndex, setImageIndex] = useState(0)
  const currentSlideContext = useOptionalCurrentSlide()
  useEffect(() => {
    currentSlideContext?.setCurrentSlideIndex(imageIndex, ExternalIntegrationEnum.Instagram)
  }, [imageIndex, currentSlideContext])
  const [imageFailed, setImageFailed] = useState(false)
  const { isMobile } = useResponsiveDesign()
  const [imagesLoading, setImagesLoading] = useState(false)
  const isMultiImage = (mediaObjects?.length ?? 0) > 1
  const [isCarouselHovered, setIsCarouselHovered] = useState(false)
  const { hoverProps: carouselHoverProps } = useHover({
    isDisabled: !isMultiImage,
    onHoverStart: () => setIsCarouselHovered(true),
    onHoverEnd: () => setIsCarouselHovered(false),
  })

  const { isTruncated, setIsTruncated, shouldShowMoreButton, textRef } = useTruncateText({
    content,
    truncatedByDefault: alwaysTruncateCaption || (!isMobile && (!!mediaObjects?.length || !!customPreviewContent)),
  })

  useEffect(() => {
    if (activePlatform && activePlatform !== ExternalIntegrationEnum.Instagram && !isTruncated) {
      setIsTruncated(true)
    }
  }, [activePlatform, isTruncated, setIsTruncated])

  const images = mediaObjects?.map((mediaObject) => mediaObject.thumbnail_url || mediaObject.source_url)

  const highlightClassNames = {
    mentionClassName: Styles.highlight,
    hashtagClassName: Styles.highlight,
  }

  const style: CSS.Properties = {
    ['--max-width' as any]: `${maxWidth}px`,
  }

  const iconSize = isSystemRedesignEnabled ? 20 : undefined

  const defaultBlankPlaceholder = (
    <BlankMediaPlaceholder
      channel={DesignChannel.InstagramPost}
      showHoverActionOverlay={showHoverActionOverlay}
      almanacFileKey={almanacFileKey}
      caption={content}
      onClose={onClose}
      aspectRatio={1}
    />
  )

  return (
    <div className={cx(Styles.post, containerClassName)} ref={postRef} data-testid="ig-preview" style={style}>
      <header className={Styles.header} ref={postPreviewHeaderRef}>
        <div className={Styles.user}>
          {imageFailed ? (
            <img className={Styles.avatar} src={Silhouette} alt="Avatar" />
          ) : (
            <img className={Styles.avatar} src={picture} alt="Avatar" onError={() => setImageFailed(true)} />
          )}

          <div className={Styles.info}>
            <div className={Styles.username}>{name}</div>
          </div>

          {onEdit && (
            <div className={Styles.editButton}>
              <Button variant="tertiary" onPress={onEdit} frontIcon={Edit1}>
                Edit
              </Button>
            </div>
          )}
          {!hideConnectButton && isMobile && <ConnectButton />}
        </div>
      </header>
      {customPreviewContent || (
        <>
          {images && images.length > 0 ? (
            <ConditionalWrapper
              condition={isMultiImage}
              wrapper={(children) => (
                <div
                  className={cx(Styles.carouselHoverTarget, PREVIEW_CAROUSEL_HOVER_TARGET)}
                  role="group"
                  aria-roledescription="carousel"
                  aria-label="Image carousel"
                  {...carouselHoverProps}
                >
                  <Carousel
                    ref={setEmbla}
                    plugins={carouselPlugins}
                    fullWidth
                    withControls={false}
                    onSlideScrolled={(e) => setImageIndex(e.selectedScrollSnap())}
                  >
                    {children}
                  </Carousel>
                  {!isGenerating && <PreviewCarouselArrows emblaApi={embla} />}
                </div>
              )}
            >
              {mediaObjects?.filter(Boolean).map((mediaObject, index) => {
                const imgSrc = mediaObject.thumbnail_url || mediaObject.source_url || ''

                return (
                  <div
                    key={`igImage-${index}-${imgSrc}`}
                    className={cx(Styles.imagePreviewContainer, {
                      [Styles.carouselSlide]: isMultiImage,
                      [Styles.designCarouselSlide]: isMultiImage && isDesignMediaObject(mediaObject),
                    })}
                  >
                    {captionElement}
                    <MediaRenderer
                      channel={DesignChannel.InstagramPost}
                      mediaObject={mediaObject}
                      alt=""
                      showHoverActionOverlay={showHoverActionOverlay}
                      onClose={onClose}
                      almanacFileKey={almanacFileKey}
                      isGenerating={isGenerating}
                      showVideoControls={showVideoControls}
                      videoFit={videoFit}
                      isActive={isActive}
                      isPressable={!showHoverActionOverlay}
                      style={{
                        aspectRatio: calculateAspectRatio(mediaObject),
                        width: '100%',
                        background: 'var(--dark-100)',
                      }}
                      allMediaObjects={mediaObjects}
                      caption={content}
                      editOverlay={
                        isMultiImage
                          ? {
                              position: 'top',
                              forceShow: isCarouselHovered,
                              gradient: true,
                            }
                          : undefined
                      }
                    />
                  </div>
                )
              })}
            </ConditionalWrapper>
          ) : (
            <>
              {(imagesLoading && !images?.length) || isInsertingMedia ? (
                <Skeleton className={Styles.imageContainer} active paragraph={{ rows: 1 }} title={false} />
              ) : (
                <>{loading ? defaultBlankPlaceholder : emptyStateContent ?? defaultBlankPlaceholder}</>
              )}
            </>
          )}
        </>
      )}
      <ConditionalWrapper
        condition={showHoverActionOverlay && almanacFileKey}
        wrapper={(children) => {
          if (!scheduledPost || !almanacFileKey) return children

          return (
            <EditCaptionOverlay
              scheduledPost={scheduledPost}
              almanacFileKey={almanacFileKey}
              borderRadius={0}
              position="top-right"
              onClose={onClose}
            >
              {children}
            </EditCaptionOverlay>
          )
        }}
      >
        <div className={Styles.postContent} ref={postPreviewContentRef}>
          <div className={Styles.actions}>
            <div className={Styles.actionsLeft}>
              <LikeIcon size={iconSize} />
              <CommentIcon size={iconSize} />
              <ShareIcon size={iconSize} />
            </div>
            {images && images.length > 1 && !customPreviewContent && (
              <CarouselDots
                count={images.length}
                activeIndex={imageIndex}
                setActiveIndex={(index: number) => {
                  embla?.scrollTo(index, true)
                  setImageIndex(index)
                }}
              />
            )}

            <div className={Styles.actionsRight}>
              <BookmarkIcon size={iconSize} />
            </div>
          </div>
          <div className={Styles.caption}>
            {(() => {
              if (isGenerating) return <CaptionSkeleton />
              if (loading && pollContentStream && !customPreviewContent) {
                return (
                  <>
                    <span className={cx(Styles.username, { [Styles.loading]: loading })}>{name}</span>
                    <PollingPreview
                      setImagesLoading={setImagesLoading}
                      imagesLoading={imagesLoading}
                      channel={ChannelType.InstagramPosts}
                    />
                  </>
                )
              }
              return (
                <>
                  {isMobile ? (
                    <>
                      <span
                        ref={textRef as React.RefObject<HTMLSpanElement>}
                        key={`caption-${content}`}
                        className={cx({ [Styles.truncated]: isTruncated })}
                        dangerouslySetInnerHTML={{
                          __html: highlightContent(`<strong>${name}</strong> ${content}`, highlightClassNames),
                        }}
                      />
                      {shouldShowMoreButton && !alwaysTruncateCaption && (
                        <MoreButton isTruncated={isTruncated} setIsTruncated={setIsTruncated} />
                      )}
                    </>
                  ) : (
                    <>
                      <div className={Styles.captionContent}>
                        {!usernameContentSameLine && (
                          <>
                            <span className={Styles.username}>{name}</span>{' '}
                          </>
                        )}
                        {usernameContentSameLine ? (
                          <span
                            ref={textRef as React.RefObject<HTMLSpanElement>}
                            key={`caption-${content}`}
                            className={cx({ [Styles.truncated]: isTruncated })}
                          >
                            <>
                              {<strong>{name}</strong>} {content}
                            </>
                          </span>
                        ) : (
                          <span
                            ref={textRef as React.RefObject<HTMLSpanElement>}
                            key={`caption-${content}`}
                            className={cx({ [Styles.truncated]: isTruncated })}
                            dangerouslySetInnerHTML={{
                              __html: highlightContent(content, highlightClassNames),
                            }}
                          />
                        )}
                      </div>
                      {shouldShowMoreButton && !alwaysTruncateCaption && (
                        <MoreButton
                          isTruncated={isTruncated}
                          setIsTruncated={() => {
                            setIsTruncated(!isTruncated)
                            onSeeMoreToggle?.(isTruncated)
                          }}
                        />
                      )}
                    </>
                  )}
                </>
              )
            })()}
          </div>
        </div>
      </ConditionalWrapper>
    </div>
  )
}
